import { Input, TextArea } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React from 'react';

import {
  trackScsKeyEditStarted,
  trackScsKeyScopeChanged,
  trackScsScopeDiffCheckTriggered,
} from '../../utils/analytics';
import { formatStoreAlias } from '../../utils/formatting';
import {
  scsReportGet,
} from '../../utils/service-calls/scs';
import { toGeo, getCountriesByGeo } from '../../utils/static/scs-geo-iso-map';
import { REQUIRED_FIELD } from '../../utils/validation/input-validation';
import { ButtonBlack, ButtonSubmit, Select } from '../reusable';

import ScopeDiffCheck, { filterStoresWithDiffVal } from './scope-diff-check/scope-diff-check';

const GEO = 'GEO';
const COUNTRY = 'COUNTRY';
const GLOBAL = 'GLOBAL';
const STORE = 'STORE';

/**
 * Gets a stable identifier for a store, with fallback logic
 * @param {Object} store - The store object
 * @returns {string} The store identifier (id, country-storeNumber composite, or 'unknown')
 */
const getStoreIdentifier = (store) => {
  if (store?.id) return store.id;
  if (store?.address?.country && store?.storeNumber) {
    return `${store.address.country}-${store.storeNumber}`;
  }
  return 'unknown';
};
class Keyset extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      diffDataMap: {},
      editing: false,
      isDiffModalLoading: false,
      isDiffModalOpen: false,
      justification: '',
      originalSaveData: null,
      scopeType: this.props.currentPair.scopeType,
      value: this.props.currentPair.value,
    };
  }

  fetchCountryList = () => {
    const countryList = [];
    if (this.state.scopeType === GLOBAL) {
      return countryList;
    }
    if (this.state.scopeType === COUNTRY) {
      countryList.push(this.props.currentStore.address.country);
    } else if (this.state.scopeType === GEO) {
      const geo = toGeo(this.props.currentStore);
      if (geo === 'EM') {
        countryList.push(this.props.currentStore.address.country);
      } else {
        const countries = getCountriesByGeo(geo);
        countries.forEach((country) => {
          if (country && country !== 'Undefined') {
            countryList.push(country);
          }
        });
      }
    }
    return countryList;
  };

  getBooleanValueString = () => (
  typeof this.state.value === 'boolean' ? this.state.value.toString() : this.state.value
);

  getErrorMessage = () => {
    const valueEmpty = this.state.value === 'Undefined';
    const justificationEmpty = !this.state.justification.trim();
    if (valueEmpty || justificationEmpty) {
      return 'Value and Justification is mandatory';
    }
    return '';
  };

  onInputChange = (value, prop = 'value') => {
    const newValue = value.target && (value.target.value || value.target.value === '')
      ? value.target.value
      : value;

    // Track scope change
    if (prop === 'scopeType' && newValue !== this.state.scopeType) {
      trackScsKeyScopeChanged({
        keyName: this.props.name,
        newScope: newValue,
        oldScope: this.state.scopeType,
      });
    }

    this.setState({ [prop]: newValue });
  };

  onProceed = async () => {
    this.setState({ isDiffModalLoading: true });
    this.setState({ isDiffModalOpen: true });

    // Format the original save data that will be combined with conflicts
    const originalSaveData = {
      body: {
        changeRequest: {
          justification: this.state.justification,
          requestedBy: this.props.userInfo?.email || '',
        },
        hierarchy: {
          brand: this.props.currentStore.brand,
          country: this.props.currentStore.address.country,
          geo: toGeo(this.props.currentStore),
        },
        key: this.props.name,
        scope: '',
        scopeType: this.state.scopeType,
        value: this.state.value,
      },
      description: this.props.description,
      group: this.props.group,
      keyType: this.props.type?.toUpperCase() || '',
    };

    // Set up scope differently depending on scopeType
    switch (this.state.scopeType) {
      case (STORE):
        originalSaveData.body.scope = formatStoreAlias(this.props.currentStore);
        break;
      case (COUNTRY):
        originalSaveData.body.scope = this.props.currentStore.address.country;
        break;
      case (GEO):
        originalSaveData.body.scope = toGeo(this.props.currentStore);
        delete originalSaveData.body.hierarchy;
        break;
      case (GLOBAL):
        originalSaveData.body.scope = GLOBAL;
        delete originalSaveData.body.hierarchy;
        break;
      default:
        break;
    }

    try {
      const response = await scsReportGet(this.props.accessToken, this.props.name, this.props.scsRegion);
      const diffVals = filterStoresWithDiffVal(
        this.state.value,
        this.state.scopeType,
        this.fetchCountryList(),
        response.body,
        this.props.allStores
      );
      // create the diffDataMap with key as the key name and value as the diffVals
      if (diffVals.length > 0) {
        const diffDataMap = {};
        diffVals.forEach((item) => {
          if (!diffDataMap[this.props.name]) {
            diffDataMap[this.props.name] = [];
          }
          diffDataMap[this.props.name].push(item);
        });

        // Track scope diff check triggered
        trackScsScopeDiffCheckTriggered({
          conflictsCount: diffVals.length,
          keyName: this.props.name,
          scopeType: this.state.scopeType,
        });

        this.setState({
          diffDataMap,
          originalSaveData,
        });
        this.setState({ isDiffModalLoading: false, isDiffModalOpen: true });
      } else {
        this.setState({ isDiffModalLoading: false, isDiffModalOpen: false });
        this.onSave();
      }
    } catch (error) {
      this.setState({ isDiffModalLoading: false, isDiffModalOpen: false });
      this.onSave();
    }
  };

  onSave = () => {
    if (this.saveDisabled()) {
      return;
    }
    const toSend = {
      body: {
        changeRequest: {
          justification: this.state.justification,
          requestedBy: this.props.userInfo?.email || '',
        },
        hierarchy: {
          brand: this.props.currentStore.brand,
          country: this.props.currentStore.address.country,
          geo: toGeo(this.props.currentStore),
        },
        key: this.props.name,
        scope: '',
        scopeType: this.state.scopeType,
        value: this.state.value,
      },
      description: this.props.description,
      group: this.props.group,
      keyType: this.props.type?.toUpperCase() || '',
    };
    // set up scope differently depending on scopeType
    switch (this.state.scopeType) {
      case (STORE):
        toSend.body.scope = formatStoreAlias(this.props.currentStore);
        break;
      case (COUNTRY):
        toSend.body.scope = this.props.currentStore.address.country;
        break;
      case (GEO):
        toSend.body.scope = toGeo(this.props.currentStore);
        delete toSend.body.hierarchy;
        break;
      case (GLOBAL):
        toSend.body.scope = GLOBAL;
        delete toSend.body.hierarchy;
        break;
      default:
        return;
    }
    this.props.onSave(toSend);
  }

  saveDisabled = () => (this.state.value === 'Undefined' || this.state.scopeType === 'Undefined' || !this.state.justification.trim());

  toggleBool = (prop) => {
    // Track when user starts editing a key
    if (prop === 'editing' && !this.state.editing) {
      trackScsKeyEditStarted({
        currentScope: this.props.currentPair.scopeType,
        currentValue: this.props.currentPair.value,
        group: this.props.group,
        keyName: this.props.name,
        storeId: getStoreIdentifier(this.props.currentStore),
      });
    }
    this.setState((prevState) => ({ [prop]: !prevState[prop] }));
  };

  render = () => (
    <article className="border bg-white mb2-sm p2-sm ta-sm-l">
      <div className="mb2-sm u-cursor-pointer" role="menuitem" tabIndex={0}>
        <span>{this.props.name} | {this.props.currentPair.value.toString().replace(/,/g, ', ').replace(/websurvey/g, 'websurvey ')}</span>
        <ButtonBlack className="fl-sm-r" isDisabled={this.props.isReadOnly} label="Edit" onClick={() => this.toggleBool('editing')} />
      </div>
      {this.state.editing && (
        <>
          <section className="ncss-row">
            {this.props.description && <p className="ncss-col-sm-12 body-2">KEY DESCRIPTION: {this.props.description}</p>}
            <p className="ncss-col-sm-10 va-sm-t body-2">This store&#39;s value is inherited from the {this.props.currentPair.scopeType} scope.</p>
          </section>
          <section className="ncss-row mt2-sm">
            <Select
              className="ncss-col-sm-12 ncss-col-md-6 va-sm-t"
              errorMessage={this.state.scopeType === 'Undefined' ? REQUIRED_FIELD : ''}
              id="scopeType"
              label="Scope Type"
              options={[GEO, COUNTRY, STORE, GLOBAL].map((value) => ({ label: value, value }))}
              value={this.state.scopeType}
              onChange={(value) => this.onInputChange(value, 'scopeType')}
            />
            {this.props.type === 'boolean'
              ? (
                <Select
                  className="ncss-col-sm-12 ncss-col-md-6 va-sm-t"
                  errorMessage={this.state.value === 'Undefined' ? REQUIRED_FIELD : ''}
                  id="value"
                  label="Value"
                  options={[
                  { label: 'true', value: 'true' },
                  { label: 'false', value: 'false' }
                ]}
                  value={this.getBooleanValueString()}
                  onChange={this.onInputChange}
                />
              )
              : (
                <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t">
                  <Input
                    label="Value"
                    style={{ zIndex: 1 }}
                    type={(this.props.type === 'integer') ? 'number' : 'text'}
                    value={this.state.value === 'Undefined' ? '' : this.state.value}
                    onChange={this.onInputChange}
                  />
                </article>
              )}
            <article className="ncss-col-sm-12 va-sm-t ta-sm-c">
              <TextArea
                errorMessage={!this.state.justification.trim() ? 'Justification is required' : ''}
                id="justification"
                label="Justification"
                placeholder="Enter justification for this configuration change..."
                rows={4}
                value={this.state.justification}
                onChange={(e) => this.onInputChange(e, 'justification')}
              />
            </article>
            {this.state.isDiffModalOpen && (
              <ScopeDiffCheck
                accessToken={this.props.accessToken}
                diffDataMap={this.state.diffDataMap}
                isLoading={this.state.isDiffModalLoading}
                isOpen={this.state.isDiffModalOpen}
                justification={this.state.justification}
                keyName={this.props.name}
                originalSaveData={this.state.originalSaveData}
                scsRegion={this.props.scsRegion}
                setIsOpen={(isOpen) => this.setState({ isDiffModalOpen: isOpen })}
                targetValue={this.state.value}
                userInfo={this.props.userInfo}
              />
            )}
            <ButtonSubmit
              className="ncss-col-sm-12 mt-8-sm ta-sm-c"
              errorMessage={this.getErrorMessage()}
              isDisabled={this.saveDisabled()}
              label="Save"
              onClick={() => {
                if (this.state.scopeType === 'STORE' || this.state.scopeType === 'GEO' || this.state.scopeType === 'GLOBAL') {
                  this.onSave();
                } else if (this.state.scopeType === 'COUNTRY') {
                  this.onProceed();
                }
              }}
            />
          </section>
        </>
      )}
    </article>
  );
}

Keyset.defaultProps = {
  currentPair: {
    scopeType: 'Undefined',
    value: 'Undefined',
  },
};

Keyset.propTypes = {
  accessToken: PropTypes.string.isRequired,
  allStores: PropTypes.arrayOf(PropTypes.shape).isRequired,
  currentPair: PropTypes.shape({
    scopeType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
  }),
  currentStore: PropTypes.shape({
    address: PropTypes.shape({
      country: PropTypes.string,
    }),
    brand: PropTypes.string,
    id: PropTypes.string,
    storeNumber: PropTypes.string,
  }).isRequired,
  description: PropTypes.string.isRequired,
  group: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  scsRegion: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  userInfo: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
};

export default Keyset;
